"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import { getNonUmbracoUrl } from "@/utils"
import styled from "@emotion/styled"
import { Box, Button, Link, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { colors, mediaQueries, sizes, useIsMobile } from "ui"
import { downloadFile } from "utils/downloadFile"
import IconWithBackground from "../common/bit/IconWithBackground"

const StyledListWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  ${mediaQueries.desktopM} {
    max-width: 1166px;
  }
  ${mediaQueries.desktopXl} {
    max-width: 1464px;
  }
`

const StyledItemsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  gap: ${sizes.baseDouble};
  ${mediaQueries.desktopM} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  ${mediaQueries.desktopXl} {
    gap: ${sizes.baseTriple};
  }
`

const StyledItemBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${sizes.baseDouble};
  background: ${colors.white};
  border-radius: ${sizes.baseSingle};
  border: 1px solid #e6e8ed;
  box-shadow: -4.48px 8.96px 33.58px 0px rgba(2, 1, 64, 0.12);
  gap: ${sizes.baseDouble};
  ${mediaQueries.tabletDown} {
    flex-direction: row;
  }
  h5 {
    text-align: start;
    margin-inline-end: auto;
  }
  ${mediaQueries.desktopM} {
    width: 49%;
  }
  ${mediaQueries.desktopXl} {
    gap: 12px;
    width: 472px;
  }
`

const StyledButtonsWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${sizes.baseDouble};
  ${mediaQueries.tabletDown} {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: ${sizes.baseSingle};
  }
`

const StyledFileButton = styled(Button)`
  color: ${colors.black};
  font-weight: 700;
  flex: 0 0 auto;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  ${mediaQueries.tabletDown} {
    font-size: 14px;
    text-decoration: underline;
    padding: 0;
  }
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  text-decoration: none;
  width: 100%;
  p {
    text-align: start;
  }
`

const StyledDownloadButton = styled(Button)`
  min-width: unset;
  padding: 0;
  ${mediaQueries.desktopM} {
    padding: 0;
  }
`

interface FormFilesListProps {
  itemsList: {
    category?: string[]
    itemName?: string
    title?: string
    link?: { name: string; url: string }
    file?: { url: string }
    icon?: { id: number; url: string }
    primaryColor: string
    url?: string
  }[]
}

const FormFilesList: React.FC<FormFilesListProps> = ({ itemsList }) => {
  const DownloadIcon = iconsMap["download"]
  const t = useTranslations("FileFinder")
  const tSeo = useTranslations("SEO")
  const isMobile = useIsMobile()
  return (
    <StyledListWrapper>
      <StyledItemsContainer>
        {itemsList?.map((item, index) => {
          const { title, link, icon, primaryColor, file, itemName } = item
          const fileUrl = file?.url || link?.url
          const fileName = itemName || link?.name || title || ""
          return (
            <StyledItemBox key={`form-file-item-${fileName}-${index}`}>
              {icon?.url && (
                <IconWithBackground
                  icon={
                    <Image
                      src={icon.url}
                      alt={`${tSeo("iconAlt")} ${fileName}`}
                      fill
                    />
                  }
                  backgroundColor={primaryColor}
                />
              )}
              <StyledButtonsWrapper>
                <StyledLink href={file?.url} target="_blank">
                  <Typography variant="h5" component="p">
                    {fileName}
                  </Typography>
                </StyledLink>
                {link?.url && (
                  <StyledFileButton
                    variant={isMobile ? "ghost" : "outlined"}
                    fullWidth
                    onClick={() =>
                      window.open(getNonUmbracoUrl(link?.url), "_blank")
                    }
                  >
                    {t("onlineSubmission")}
                  </StyledFileButton>
                )}
              </StyledButtonsWrapper>
              <StyledDownloadButton
                variant="ghost"
                color="primary"
                href={file?.url}
                onClick={(e) => {
                  e.preventDefault()
                  if (file?.url) {
                    downloadFile(file?.url, fileName).catch(console.error)
                  }
                }}
                aria-label={`הורדת ${fileName}`}
                disabled={!fileUrl}
              >
                <DownloadIcon />
              </StyledDownloadButton>
            </StyledItemBox>
          )
        })}
      </StyledItemsContainer>
    </StyledListWrapper>
  )
}

export default FormFilesList
